import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarClock,
  CheckCircle2,
  Clock,
  GitMerge,
  ListChecks,
  Loader2,
  Plus,
  Radio,
  Trash2,
  Users,
} from 'lucide-react';
import { useAuth } from '../Auth';
import { apiService } from '../../services/api';

const BOARD_REFRESH_MS = 30000;

const STATUS_CHIPS = {
  pending: { label: 'En attente', className: 'bg-slate-100 text-slate-600 border-slate-200', Icon: Clock },
  on_agenda: { label: "À l'ordre du jour", className: 'bg-indigo-50 text-indigo-700 border-indigo-100', Icon: CalendarClock },
  treated: { label: 'Traité', className: 'bg-emerald-50 text-emerald-700 border-emerald-100', Icon: CheckCircle2 },
  converted: { label: 'Transformé en tension', className: 'bg-violet-50 text-violet-700 border-violet-100', Icon: GitMerge },
};

const formatShortDate = (value) => {
  if (!value) return '';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const StatusChip = ({ status }) => {
  const chip = STATUS_CHIPS[status] || STATUS_CHIPS.pending;
  const { Icon } = chip;
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${chip.className}`}>
      <Icon size={11} />
      {chip.label}
    </span>
  );
};

const PointRow = ({ point, status, meta, onDelete, deleteDisabledReason, muted = false }) => (
  <li className={`flex items-start justify-between gap-3 rounded-xl border px-3 py-2.5 ${muted ? 'border-slate-100 bg-slate-50/60' : 'border-slate-200 bg-white'}`}>
    <div className="min-w-0 flex-1">
      <p className={`text-sm font-medium break-words ${muted ? 'text-slate-500' : 'text-slate-800'}`}>{point.title}</p>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <StatusChip status={status} />
        {meta && <span className="text-[11px] text-slate-400">{meta}</span>}
      </div>
    </div>
    {onDelete && (
      <button
        type="button"
        onClick={() => onDelete(point.id)}
        disabled={Boolean(deleteDisabledReason)}
        title={deleteDisabledReason || 'Supprimer ce point'}
        aria-label="Supprimer ce point"
        className="shrink-0 rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400"
      >
        <Trash2 size={14} />
      </button>
    )}
  </li>
);

const SectionTitle = ({ icon: Icon, children, count }) => (
  <div className="flex items-center justify-between">
    <h3 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">
      {Icon && <Icon size={13} />}
      {children}
    </h3>
    {count !== undefined && <span className="text-[10px] font-semibold text-slate-400">{count}</span>}
  </div>
);

// Onglet « Points » : ce que chacun veut aborder à la prochaine réunion tactique du cercle.
const PointsPanel = ({
  selectedCircle,
  points = [],
  pointsLoading,
  pointsError,
  onCreatePoint,
  onDeletePoint,
  selectedCircleIsRole = false,
}) => {
  const { getAuthHeaders } = useAuth();
  const [draftTitle, setDraftTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const [board, setBoard] = useState(null);
  const [boardError, setBoardError] = useState(null);
  const requestSeq = useRef(0);
  const circleId = selectedCircle?.id ?? null;
  const canAddPoint = Boolean(circleId) && !selectedCircleIsRole;
  const pointsKey = useMemo(() => (points || []).map((point) => point.id).join(','), [points]);

  const fetchBoard = useCallback(async () => {
    if (!circleId || selectedCircleIsRole) {
      setBoard(null);
      return;
    }
    const seq = ++requestSeq.current;
    try {
      const response = await fetch(`${apiService.getBaseUrl()}/circle-points/board?circle_id=${circleId}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Statut des points indisponible');
      const data = await response.json();
      if (seq !== requestSeq.current) return;
      setBoard(data && typeof data === 'object' ? data : null);
      setBoardError(null);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setBoardError(err.message);
    }
  }, [circleId, selectedCircleIsRole, getAuthHeaders]);

  useEffect(() => {
    setBoard(null);
    setBoardError(null);
  }, [circleId]);

  // Rechargé quand mes points changent (ajout, suppression) et régulièrement pour suivre la réunion en cours.
  useEffect(() => {
    fetchBoard();
    const id = setInterval(fetchBoard, BOARD_REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchBoard, pointsKey]);

  const boardMatchesCircle = board && String(board.circle_id) === String(circleId);
  const myPoints = useMemo(() => {
    const listed = Array.isArray(points) ? points : [];
    if (!boardMatchesCircle || !Array.isArray(board.mine)) {
      return listed.map((point) => ({ ...point, status: 'pending' }));
    }
    // La liste de la page fait foi (ajout ou suppression à l'instant) ; le tableau apporte les statuts.
    const statusById = new Map(board.mine.map((point) => [String(point.id), point.status]));
    if (listed.length === 0) return board.mine;
    return listed.map((point) => ({ ...point, status: statusById.get(String(point.id)) || 'pending' }));
  }, [board, boardMatchesCircle, points]);
  const recentPoints = boardMatchesCircle && Array.isArray(board.recent) ? board.recent : [];
  const otherPoints = useMemo(
    () => (boardMatchesCircle && Array.isArray(board.others) ? board.others : []),
    [board, boardMatchesCircle]
  );
  const othersByAuthor = useMemo(() => {
    const groups = new Map();
    otherPoints.forEach((point) => {
      const key = String(point.author_id);
      if (!groups.has(key)) groups.set(key, { authorId: key, name: point.author_name || 'Membre', items: [] });
      groups.get(key).items.push(point);
    });
    return Array.from(groups.values()).sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  }, [otherPoints]);
  const openMeeting = boardMatchesCircle ? board.open_meeting : null;
  const circleName = selectedCircle?.name || 'ce cercle';

  const handleAddPoint = async () => {
    const trimmed = (draftTitle || '').trim();
    if (!trimmed || !canAddPoint || adding) return;
    setAdding(true);
    try {
      const created = await (onCreatePoint && onCreatePoint(trimmed));
      if (created) setDraftTitle('');
    } finally {
      setAdding(false);
    }
  };

  const errorMessage = pointsError || null;

  return (
    <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm h-full overflow-hidden flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
          <ListChecks size={18} />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Points à aborder</p>
          <h2 className="text-lg font-bold leading-snug text-slate-900">
            Points pour la prochaine réunion tactique {selectedCircle?.name ? `de ${circleName}` : ''}
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            Note ici ce que tu veux aborder. Le secrétaire les ajoute à l'ordre du jour ; un point clos en réunion
            sort de la liste.
          </p>
        </div>
      </div>

      {openMeeting && (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
          <Radio size={14} className="shrink-0" />
          Réunion tactique en cours : tes points en attente peuvent être ajoutés à l'ordre du jour.
        </div>
      )}

      {errorMessage && (
        <div className="rounded-xl border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-600">
          {errorMessage}
        </div>
      )}

      <div className="flex gap-2">
        <input
          type="text"
          value={draftTitle}
          onChange={(e) => setDraftTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddPoint();
            }
          }}
          className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-200 disabled:bg-slate-50"
          placeholder={
            !circleId
              ? 'Sélectionne un cercle pour préparer un point'
              : selectedCircleIsRole
                ? 'Sélectionne un cercle (pas un rôle) pour préparer un point'
                : 'Un point à aborder… (Entrée pour ajouter)'
          }
          disabled={!canAddPoint || adding}
          maxLength={500}
        />
        <button
          type="button"
          onClick={handleAddPoint}
          className="inline-flex shrink-0 items-center gap-1 rounded-xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-40"
          disabled={!canAddPoint || !draftTitle.trim() || adding}
        >
          {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Ajouter
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto space-y-5 pr-1">
        <section className="space-y-2">
          <SectionTitle count={myPoints.length}>Mes points</SectionTitle>
          {pointsLoading && myPoints.length === 0 ? (
            <p className="flex items-center gap-2 text-sm text-slate-400">
              <Loader2 size={14} className="animate-spin" /> Chargement…
            </p>
          ) : myPoints.length === 0 ? (
            <p className="rounded-xl border border-dashed border-slate-200 px-3 py-4 text-center text-xs text-slate-400">
              {canAddPoint ? 'Aucun point en attente. Ajoute ce que tu veux aborder.' : 'Aucun point.'}
            </p>
          ) : (
            <ul className="space-y-2">
              {myPoints.map((point) => (
                <PointRow
                  key={point.id}
                  point={point}
                  status={point.status}
                  meta={point.created_at ? `ajouté le ${formatShortDate(point.created_at)}` : null}
                  onDelete={onDeletePoint}
                  deleteDisabledReason={point.status === 'on_agenda'
                    ? "Déjà à l'ordre du jour de la réunion en cours"
                    : null}
                />
              ))}
            </ul>
          )}
        </section>

        {recentPoints.length > 0 && (
          <section className="space-y-2">
            <SectionTitle count={recentPoints.length}>Traités récemment</SectionTitle>
            <ul className="space-y-2">
              {recentPoints.map((point) => (
                <PointRow
                  key={`recent-${point.id}`}
                  point={point}
                  status={point.status === 'converted' ? 'converted' : 'treated'}
                  meta={point.updated_at ? `le ${formatShortDate(point.updated_at)}` : null}
                  muted
                />
              ))}
            </ul>
          </section>
        )}

        {boardMatchesCircle && board.can_see_all && (
          <section className="space-y-2">
            <SectionTitle icon={Users} count={otherPoints.length}>Points des autres membres</SectionTitle>
            {othersByAuthor.length === 0 ? (
              <p className="text-xs text-slate-400">Personne d'autre n'a encore préparé de point.</p>
            ) : (
              <div className="space-y-3">
                {othersByAuthor.map((group) => (
                  <div key={group.authorId} className="space-y-1.5">
                    <p className="text-xs font-semibold text-slate-600">{group.name}</p>
                    <ul className="space-y-1.5">
                      {group.items.map((point) => (
                        <PointRow
                          key={point.id}
                          point={point}
                          status={point.status}
                          meta={point.created_at ? `ajouté le ${formatShortDate(point.created_at)}` : null}
                        />
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {boardError && !boardMatchesCircle && canAddPoint && (
          <p className="text-[11px] text-slate-400">Statut des points indisponible pour le moment.</p>
        )}
      </div>
    </div>
  );
};

export default PointsPanel;
